import Vue from 'vue';

import ChatService from '@/services/API/ChatService';
import CourseService from '@/services/API/CourseService';
import ContractService from '@/services/API/ContractsService';

import { appChat } from '@/plugins/app-chat';

import { ATTESTATION_STATUSES } from '@/modules/CourseManagement/constants';
import { COURSE_STATUSES, COURSE_TYPES } from '@/utils/constants';

import Base from '../Base';
import Unit from './Unit';
import Chat from './Chat';

export default class Course extends Base {
  constructor(item) {
    super(item.id, '/courses');

    this.can_edit = item.edit;

    this.title = item.title;
    this.telegram = item.telegram;
    this.short_title = item.short_title;
    this.description = item.description;
    this.status = item.status;
    this.duration = item.duration;
    this.type = item.type;
    this.created_at = item.created_at;
    this.age_min = item.age_min;
    this.age_max = item.age_max;
    this.price = item.price;
    this.type_activity = item.type_activity;
    this.level_type = item.level_type;
    this.price = item.price;
    this.contract = null;
    this.directory_professional_activity_id = item.directory_professional_activity_id;
    this.directory_specialities_group_id = item.directory_specialities_group_id;

    // Deprecated
    this.groups = [];
    this.image = item.image;

    // Current user related fields
    this.rules = item.rules || {};
    this.chat_room = item.chat_room;
    this.user_group_id = null;
    this.attributes_filled = true;
    this.user_prior_records = item.user_prior_records;
    this.priority_user_prior_record = item.priority_user_prior_record;
    // this.prior_record_status = item.prior_record_status;
    this.prior_record_status = item?.priority_user_prior_record?.user_record?.status
      || item.prior_record_status;

    // Expand fields
    this.prior_records = item.prior_records || [];
    this.my_prior_records_30 = item.my_prior_records_30 || [];
    this.my_active_prior_records_30 = item.my_active_prior_records_30 || [];
    this.course_units_count = item.course_units_count;
    this.units = item.course_units
      ? item.course_units.map((i) => new Unit(
        {
          ...i,
          course_id: item.id,
          relativeView: true,
        },
      ))
      : [];
    this.tasks = item.tasks;
    this.organization = item.organization;

    // Custom
    this.hasConsultationMessages = null;
    this.directionActivity = {
      direction: item.direction,
      type_activity: item?.type_activity?.id,
    };
    this.setupUserGroup();
  }

  setupUserGroup() {
    const group = this.priority_user_prior_record?.user_record?.group;
    if (group) {
      this.user_group_id = group.id;
    }
  }

  get isQualification() {
    return this.type === COURSE_TYPES.qualification;
  }

  get hasPermissions() {
    // const prepayment = this.contract ? this.contract?.isPaymentPrePayment : true;
    return {
      consultations: [
        ATTESTATION_STATUSES.enrolled,
        ATTESTATION_STATUSES.allowedAttestation,
        null,
      ].includes(this.prior_record_status),
      passTasks: ([
        ATTESTATION_STATUSES.enrolled,
        ATTESTATION_STATUSES.allowedAttestation,
        ATTESTATION_STATUSES.attestated,
      ].includes(this.prior_record_status)),
    };
  }

  get unitsPassed() {
    return this.units.filter((i) => i.user_passed);
  }

  get progress() {
    return (100 / this.units.length) * this.units.length;
  }

  get statusColor() {
    if (this.unitsPassed.length > 0) {
      return this.user_passed ? 'success' : 'warning lighten-2';
    }
    return 'grey';
  }

  get priorRecordActive() {
    return this.prior_records.filter(({ end_learning: end }) => {
      const day = 86400000;
      const now = new Date().getTime();
      const endLearning = new Date(end).getTime() + day;
      return endLearning >= now;
    });
  }

  async fetchToken() {
    const resp = await CourseService.getCourseToken(this.id);
    this.token = resp.data.data;
  }

  async fetchGroups() {
    const resp = await CourseService.getCourseGroupList(this.id);
    this.groups = resp.data.data;
  }

  async fetchUnits(readOnly, params) {
    const { isAuthenticated } = Vue.user;
    const method = !isAuthenticated || readOnly ? 'publicCourseUnitIndex' : 'courseUnitIndex';

    const { items } = await CourseService[method](
      this.id,
      {
        ...params,
        'per-page': -1,
        'search[course_id]': this.id,
        expand: `lessons,${params ? params.expand : ''}`,
      },
    );
    this.units = items;
  }

  async checkAttributes() {
    const resp = await CourseService.checkAttributes(this.priority_user_prior_record.id);
    const { is_enrollment_scan_required: needScan } = this.priority_user_prior_record;
    const { attributes_filled: attr, exists_scan: scan } = resp.data.data;
    this.attributes_filled = needScan ? scan && attr : attr;
  }

  async fetchUnread({ topConsultation, userId } = {}) {
    const tag = {
      course_id: this.id,
    };
    if (topConsultation) {
      tag.course_unit_id = null;
      tag.lesson_id = null;
    }
    const response = await ChatService.getUnreadConsultationsByTag(tag, userId);
    this.hasConsultationMessages = response.data.data;
  }

  async fetchContract(userId) {
    const { items } = await ContractService.getContractsModel({
      expand: 'transactions,qr_code,typical_contract,users,listener_preview_document,document',
      'search[is_active]': true,
      'search[user_id]': userId,
      'search[prior_record_id]': this.priority_user_prior_record.id,
      // 'search[owner_or_user_id]': this.$user.data.id,
      'search[course_id]': this.id,
    });
    [this.contract] = items;
  }

  async openChatAsStudent() {
    const room = await ChatService.getRoomCourse(this.id);
    this.startChat({
      room: room.data.data,
      hideInitials: true,
    });
  }

  async openChatAsTeacher({ replyTo }) {
    this.startChat({
      room: this.chat_room,
      replyTo,
    });
  }

  async startChat({ room, hideInitials, replyTo }) {
    const tag = {
      course_id: this.id,
    };
    const chat = new Chat({
      ...room,
      tag,
      hideInitials,
      replyTo,
    });
    appChat.set(chat, {
      opened: true,
      title: 'Консультация по организационным вопросам',
      // subtitle: this.title,
    });
    this.hasConsultationMessages = false;
  }

  async activate() {
    const status = COURSE_STATUSES.active;
    await CourseService.updateStatus(this.id, { status });
    Vue.toasted.success('Курс успешно активирован');
    this.status = status;
  }
}
